// @flow
import React, { Fragment, useState, useEffect } from 'react';
import { isEnterKey } from '../../../../utils/KeyboardUtils';
import {
	CS_InputRange,
	CS_Space,
	CS_Text,
	CS_AutoCompletePlaces,
	CS_ToggleSwitch,
	CS_Row,
} from '../../visual_components';
import {
	searchWithFilter,
	getAddressFromCoordinates,
	getQueryString,
	decodeAddress,
} from '../jobs-filter/CareersPageFilterService';
import { getBrowserLocation } from '../../careerPageUtils';
import type { CareersPageLocationRadiusSearchFilter } from '../jobs-filter/CareersPageFilterService';

type LocationFilterProps = {
	filters: CareersPageLocationRadiusSearchFilter,
};

export default function LocationFilter({ filters }: LocationFilterProps) {
	const { unit, radius } = filters;
	const [selected_location, onLocationChange] = useState({
		latitude: filters.latitude,
		longitude: filters.longitude,
		address: getQueryString('loc') || '',
	});
	const [is_loading, setLoading] = useState(false);
	useEffect(() => {
		const address = getQueryString('loc');
		if (filters.latitude && filters.longitude && !address) {
			fetchAddressForCoordinates(filters);
		}
	}, []);

	function getCurrentLocation(pos) {
		if (pos) {
			fetchAddressForCoordinates(pos);
		}
		setLoading(false);
	}

	function fetchAddressForCoordinates(coords) {
		getAddressFromCoordinates(coords).then(({ results }) => {
			if (results.length) {
				onLocationChange({
					latitude: coords.latitude,
					longitude: coords.longitude,
					address: encodeURIComponent(results[0].formatted_address),
				});
				document.querySelectorAll('[name=loc]')[0].value = results[0].formatted_address;
			}
		});
	}

	const onPlaceSelected = place => {
		const location = place.geometry.location;
		const latitude = location.lat();
		const longitude = location.lng();
		onLocationChange({ longitude, latitude, address: encodeURIComponent(place.formatted_address) });
	};

	const { longitude, latitude } = selected_location;
	useEffect(() => {
		if (longitude !== filters.longitude || latitude !== filters.latitude) {
			searchWithFilter();
		}
	}, [selected_location]);

	function onRadiusChange(e) {
		const { value } = e.currentTarget;
		const selected_radius = parseInt(value, 10);
		const current_radius = parseInt(radius, 10);
		if (current_radius !== selected_radius) {
			searchWithFilter();
		}
	}

	function handleKeyDown(e) {
		if (isEnterKey(e)) {
			e.preventDefault();
			onRadiusChange(e);
		}
	}

	const max_distance_value = 120;
	const is_coords_available = latitude && longitude;
	const decoded_address = decodeAddress(selected_location.address);
	return (
		<Fragment>
			<CS_Row space_between={true} vcenter={true}>
				<CS_Text color="darker">Location</CS_Text>
			</CS_Row>
			<CS_Space vertical size={3} />
			<CS_AutoCompletePlaces
				onPlaceSelected={onPlaceSelected}
				defaultValue={decoded_address}
				onIconClick={() => {
					setLoading(true);
					getBrowserLocation(getCurrentLocation);
				}}
				is_loading={is_loading}
			/>
			<CS_Space vertical size={3} />
			{is_coords_available ? (
				<Fragment>
					<input name="rsla" value={latitude} hidden />
					<input name="rslo" value={longitude} hidden />
				</Fragment>
			) : null}
			<CS_InputRange
				min={5}
				max={max_distance_value}
				step={5}
				defaultValue={radius}
				name="rsr"
				onMouseUp={onRadiusChange}
				onTouchEnd={onRadiusChange}
				onKeyDown={handleKeyDown}
				disabled={!is_coords_available}
			/>
			<CS_Space vertical size={6} />
			<CS_ToggleSwitch
				value={unit}
				options={[{ name: 'Miles', key: 'mile' }, { name: 'Km', key: 'km' }]}
				input_key="rsu"
				disabled={!is_coords_available}
			/>
			<CS_Space vertical size={5} />
			<div className="rb-divider" />
			<CS_Space vertical size={5} />
		</Fragment>
	);
}
